<template>
  <div class="row justify-between items-center q-pa-sm bg-grey-2 border-bottom sticky-top">
    <div class="text-caption text-grey-7 text-weight-bold">HITTING REPORTS</div>
    <q-btn
      icon="print"
      label="Print Report"
      color="primary"
      unelevated
      size="sm"
      @click="printHittingSummary"
      :disable="activeHittingStats.hitters.length === 0"
    />
  </div>

  <!-- TEAM SPRAY CHART -->
  <div class="column items-center q-pa-md bg-white border-bottom">
    <div class="text-subtitle2 text-weight-bold text-primary q-mb-sm text-uppercase">
      Team Batted Balls
    </div>
    <svg
      viewBox="0 0 100 100"
      width="120"
      height="120"
      style="background-color: #f5f5f5; border-radius: 4px"
    >
      <path
        d="M 7.6 42.6 Q 50 15 92.4 42.6 L 50 85 Z"
        fill="white"
        stroke="#ccc"
        stroke-width="1.5"
      />
      <path d="M 30 65 Q 50 50 70 65 L 50 85 Z" fill="#eee" stroke="#ccc" stroke-width="1" />
      <path
        d="M 50 82 L 40 72 L 50 62 L 60 72 Z"
        fill="none"
        stroke="#ccc"
        stroke-width="1"
      />
      <circle
        v-for="(hit, idx) in activeHittingStats.teamBattedBalls"
        :key="'team-hit-' + idx"
        :cx="50 + hit.x * 75"
        :cy="85 + hit.y * 75"
        r="3.5"
        :fill="hit.color"
        stroke="#2c3e50"
        stroke-width="0.5"
        opacity="0.85"
      />
    </svg>
  </div>

  <!-- HITTER LIST -->
  <q-list separator>
    <q-expansion-item
      v-for="hitter in activeHittingStats.hitters"
      :key="'hitter-' + hitter.id"
      :default-opened="hitter.plateAppearances.length > 0"
    >
      <template v-slot:header>
        <q-item-section avatar>
          <q-avatar color="primary" text-color="white" size="sm" font-size="0.75rem">
            {{ hitter.uniformNumber }}
          </q-avatar>
        </q-item-section>
        <q-item-section>
          <div class="text-weight-bold">{{ hitter.name }}</div>
        </q-item-section>
        <q-item-section side>
          <q-badge :color="hitter.plateAppearances.length > 0 ? 'accent' : 'grey-4'">
            {{ hitter.plateAppearances.length }} PA
          </q-badge>
        </q-item-section>
      </template>

      <q-card class="bg-grey-1">
        <q-card-section class="q-py-sm q-px-md">
          <!-- Overall Pitches Seen -->
          <div class="text-caption q-mb-md">
            <div class="text-weight-bold text-primary text-uppercase q-mb-xs">
              Overall Pitches Seen: {{ hitter.overall.total }}
            </div>
            <div class="row q-gutter-x-sm">
              <span class="text-positive text-weight-bold">Balls: {{ hitter.overall.balls }}</span>
              <span class="text-negative text-weight-bold">SwStr: {{ hitter.overall.swingingStrikes }}</span>
              <span class="text-orange-9 text-weight-bold">CStr: {{ hitter.overall.calledStrikes }}</span>
              <span class="text-grey-8 text-weight-bold">Foul: {{ hitter.overall.fouls }}</span>
              <span class="text-info text-weight-bold">In Play: {{ hitter.overall.inPlay }}</span>
              <span class="text-deep-purple text-weight-bold" v-if="hitter.overall.droppedThirds > 0">D3: {{ hitter.overall.droppedThirds }}</span>
              <span class="text-teal text-weight-bold" v-if="hitter.overall.bunts > 0">Bunts: {{ hitter.overall.bunts }}</span>
            </div>
          </div>

          <!-- Per PA Breakdown -->
          <div
            v-for="pa in hitter.plateAppearances"
            :key="pa.id"
            class="row items-center q-py-sm"
            style="border-top: 1px solid #e0e0e0"
          >
            <!-- Left Side: PA Details & Counts -->
            <div class="col-6 q-pr-sm">
              <div class="text-weight-bold text-body2">
                Inn {{ pa.inning }} vs {{ pa.pitcherName }}
              </div>
              <div class="text-primary text-weight-bold q-mb-xs">Result: {{ pa.result }}</div>
              <div class="text-caption text-grey-8">
                <div>
                  Pitches Seen: <strong>{{ pa.stats.total }}</strong>
                </div>
                <div>
                  B: {{ pa.stats.balls }} | SwStr: {{ pa.stats.swingingStrikes }} | CStr: {{ pa.stats.calledStrikes }} | Foul: {{ pa.stats.fouls }}
                  <template v-if="pa.stats.droppedThirds > 0"> | D3: {{ pa.stats.droppedThirds }}</template>
                  <template v-if="pa.stats.bunts > 0"> | Bunts: {{ pa.stats.bunts }}</template>
                </div>
              </div>
            </div>

            <!-- Right Side: Triple PA Charts -->
            <div class="col-6 row q-gutter-xs justify-end no-wrap">
              <!-- PA Pitch Grid -->
              <div class="bg-white border-all rounded-borders q-pa-xs shadow-1 column items-center">
                <div class="text-primary text-weight-bold" style="font-size: 0.5rem; letter-spacing: 0.2px">
                  PITCHES
                </div>
                <svg viewBox="0 0 100 100" width="45" height="45" style="background-color: #f5f5f5; border-radius: 4px">
                  <rect x="20" y="25" width="60" height="50" fill="white" stroke="#333" stroke-width="1.5" />
                  <line x1="20" y1="41.6" x2="80" y2="41.6" stroke="#999" stroke-width="0.5" />
                  <line x1="20" y1="58.3" x2="80" y2="58.3" stroke="#999" stroke-width="0.5" />
                  <line x1="40" y1="25" x2="40" y2="75" stroke="#999" stroke-width="0.5" />
                  <line x1="60" y1="25" x2="60" y2="75" stroke="#999" stroke-width="0.5" />
                  <circle
                    v-for="(p, idx) in pa.pitches"
                    :key="'mini-p-' + idx"
                    :cx="20 + p.x * 60"
                    :cy="25 + p.y * 50"
                    r="4"
                    :fill="p.color"
                    stroke="#2c3e50"
                    stroke-width="0.5"
                    opacity="0.85"
                  />
                </svg>
              </div>

              <!-- PA Spray Chart -->
              <div class="bg-white border-all rounded-borders q-pa-xs shadow-1 column items-center">
                <div class="text-primary text-weight-bold" style="font-size: 0.5rem; letter-spacing: 0.2px">
                  BATTED
                </div>
                <svg viewBox="0 0 100 100" width="45" height="45" style="background-color: #f5f5f5; border-radius: 4px">
                  <path d="M 7.6 42.6 Q 50 15 92.4 42.6 L 50 85 Z" fill="white" stroke="#ccc" stroke-width="1.5" />
                  <path d="M 30 65 Q 50 50 70 65 L 50 85 Z" fill="#eee" stroke="#ccc" stroke-width="1" />
                  <path d="M 50 82 L 40 72 L 50 62 L 60 72 Z" fill="none" stroke="#ccc" stroke-width="1" />
                  <circle
                    v-for="(hit, idx) in pa.battedBalls"
                    :key="'mini-h-' + idx"
                    :cx="50 + hit.x * 75"
                    :cy="85 + hit.y * 75"
                    r="4"
                    :fill="hit.color"
                    stroke="#2c3e50"
                    stroke-width="0.5"
                    opacity="0.85"
                  />
                </svg>
              </div>

              <!-- PA Runners Context -->
              <div class="bg-white border-all rounded-borders q-pa-xs shadow-1 column items-center">
                <div class="text-primary text-weight-bold" style="font-size: 0.5rem; letter-spacing: 0.2px">
                  RUNNERS
                </div>
                <svg viewBox="0 0 100 100" width="45" height="45" style="background-color: #f5f5f5; border-radius: 4px">
                  <path d="M 50 85 L 25 60 L 50 35 L 75 60 Z" fill="none" stroke="#ccc" stroke-width="1.5" />
                  <circle v-if="pa.runners.includes('1')" cx="75" cy="60" r="6" fill="#f39c12" stroke="#e67e22" stroke-width="1" />
                  <circle v-if="pa.runners.includes('2')" cx="50" cy="35" r="6" fill="#f39c12" stroke="#e67e22" stroke-width="1" />
                  <circle v-if="pa.runners.includes('3')" cx="25" cy="60" r="6" fill="#f39c12" stroke="#e67e22" stroke-width="1" />
                </svg>
              </div>
            </div>
          </div>

          <div
            v-if="hitter.plateAppearances.length === 0"
            class="text-caption text-grey-6 text-italic"
          >
            No plate appearances recorded today.
          </div>
        </q-card-section>
      </q-card>
    </q-expansion-item>

    <div
      v-if="activeHittingStats.hitters.length === 0"
      class="q-pa-xl text-center text-grey-6"
    >
      <q-icon name="sports_baseball" size="xl" color="grey-4" class="q-mb-sm" />
      <div>No hitting data recorded yet.</div>
    </div>
  </q-list>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import type {
  PitchTransaction,
  PlayTransaction, RunnerAdvanceTransaction,
} from 'src/stores/base-types'

const gameStore = useGameStore()
const settingsStore = useSettingsStore()

const props = defineProps<{
  teamLineup: 'home' | 'away' | 'all'
}>()

// --- HITTING STATS LOGIC ---
const activeHittingStats = computed(() => {
  const lineup = props.teamLineup === 'home' ? gameStore.homeLineup : gameStore.visitingLineup
  if (!lineup) return { hitters: [], teamBattedBalls: [] }

  const allPlayers = lineup.concat(lineup.flatMap((l) => l.substitutions || []))
  const teamBattedBalls: { x: number; y: number; color: string }[] = []
  const hittersMap = new Map()

  const allLinkedPlays = gameStore.transactions.filter(
    (t) => t.eventType === 'PLAY',
  ) as PlayTransaction[]
  const allOutcomes = [
    ...(settingsStore.playAllOutcomes || []),
    ...(settingsStore.primaryPitchCategories || []),
    ...(settingsStore.pitchAdvancedOptions || []),
  ]

  // Helper to find Pitcher Name
  const getPitcherName = (id: number) => {
    const oppLineup = props.teamLineup === 'home' ? gameStore.visitingLineup : gameStore.homeLineup
    const oppPlayers = oppLineup
      ? oppLineup.concat(oppLineup.flatMap((l) => l.substitutions || []))
      : []
    const found = oppPlayers.find((p) => p.player.id === id)
    return found ? `#${found.player.uniformNumber || '-'} ${found.player.name}` : `Unknown (${id})`
  }

  allPlayers.forEach((entry) => {
    const batterId = entry.player.id
    if (!batterId) return

    const playerPAs = gameStore.plateAppearances.filter((pa) => pa.batterId === batterId)
    if (playerPAs.length === 0) return

    const hitterData = {
      id: batterId,
      name: entry.player.name,
      uniformNumber: entry.player.uniformNumber || '-',
      overall: { total: 0, balls: 0, calledStrikes: 0, swingingStrikes: 0, fouls: 0, inPlay: 0, droppedThirds: 0, bunts: 0 },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      plateAppearances: [] as any[],
    }

    playerPAs.forEach((pa) => {
      const paPitches = gameStore.transactions.filter(
        (t) => t.eventType === 'PITCH' && t.plateAppearanceId === pa.id,
      ) as PitchTransaction[]

      // Calculate runners on base at the START of this PA
      const inningPAs = gameStore.plateAppearances.filter(p => p.inning === pa.inning && p.halfInning === pa.halfInning)
      const priorPAs = inningPAs.filter(p => p.paNumber < pa.paNumber)
      const priorPAIds = priorPAs.map(p => p.id)

      const priorAdvances = gameStore.transactions.filter(
        t => t.eventType === 'RUNNER_ADVANCE' && priorPAIds.includes(t.plateAppearanceId)
      ) as RunnerAdvanceTransaction[]

      const runnerPositions = new Map<number, string>()
      priorAdvances.sort((a,b) => a.sequence - b.sequence).forEach(adv => {
        if (adv.isOut || adv.runsScored > 0) {
          runnerPositions.delete(adv.runnerId)
        } else if (['1', '2', '3'].includes(adv.endBase)) {
          runnerPositions.set(adv.runnerId, adv.endBase)
        }
      })

      const paData = {
        id: pa.id,
        inning: pa.inning,
        paNumber: pa.paNumber,
        pitcherName: getPitcherName(pa.pitcherId),
        result: 'Unknown',
        stats: { total: 0, balls: 0, calledStrikes: 0, swingingStrikes: 0, fouls: 0, inPlay: 0, droppedThirds: 0, bunts: 0 },
        pitches: [] as { x: number; y: number; color: string }[],
        battedBalls: [] as { x: number; y: number; color: string }[],
        runners: Array.from(runnerPositions.values())
      }

      // Look for a play first, otherwise fall back to the final pitch's outcome
      const terminalPlay = allLinkedPlays.find((pl) => pl.plateAppearanceId === pa.id)
      const finalPitchOutcome =
        paPitches[paPitches.length - 1]?.outcome ||
        paPitches[paPitches.length - 1]?.pitchOutcome ||
        ''

      let terminalOutcome = terminalPlay?.outcome || finalPitchOutcome

      paPitches.forEach((p) => {
        const outcome = p.pitchOutcome || 'B'
        let color = '#7f8c8d'

        hitterData.overall.total++
        paData.stats.total++

        if (['B', 'W', 'H', 'I', 'V', 'P'].includes(outcome)) {
          hitterData.overall.balls++
          paData.stats.balls++
          color = '#27ae60' // Green
        } else if (['C', 'A'].includes(outcome)) {
          hitterData.overall.calledStrikes++
          paData.stats.calledStrikes++
          color = '#e67e22' // Orange
        } else if (['S', 'K', 'Q', 'M', 'D3'].includes(outcome)) {
          hitterData.overall.swingingStrikes++
          paData.stats.swingingStrikes++
          color = '#e74c3c' // Red
        } else if (['F', 'L', 'O', 'R', 'T'].includes(outcome)) {
          hitterData.overall.fouls++
          paData.stats.fouls++
          color = '#f1c40f' // Yellow
        } else if (['X', 'Y'].includes(outcome)) {
          hitterData.overall.inPlay++
          paData.stats.inPlay++
          color = '#2980b9' // Blue
        }

        // Specifically tally Bunts & Dropped 3rd Strikes
        if (outcome === 'D3') {
          hitterData.overall.droppedThirds++
          paData.stats.droppedThirds++
        }
        if (['M', 'L'].includes(outcome)) {
          hitterData.overall.bunts++
          paData.stats.bunts++
        }

        paData.pitches.push({
          x: 1 - (p.pitchX ?? 0.5), // Pitcher POV
          y: p.pitchY ?? 0.5,
          color,
        })

        if (p.hitLocationX != null && p.hitLocationY != null) {
          const linkedPlay = allLinkedPlays.find((pl) => String(pl.linkedPitchId) === String(p.id))
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const resolvedOutcome = linkedPlay?.outcome || (p as any).outcome || outcome
          if (!terminalOutcome) terminalOutcome = resolvedOutcome

          const isHit = ['S', 'D', 'T', 'HR', '1B', '2B', '3B'].includes(resolvedOutcome)
          const bbColor = isHit ? '#2980b9' : '#e74c3c' // Safe vs Out color

          const bb = { x: p.hitLocationX, y: p.hitLocationY, color: bbColor }
          paData.battedBalls.push(bb)
          teamBattedBalls.push(bb)
        }
      })

      const foundLabel = allOutcomes.find((o) => o.value === terminalOutcome)?.label
      paData.result = foundLabel || terminalOutcome || 'Incomplete'

      hitterData.plateAppearances.push(paData)
    })

    hittersMap.set(batterId, hitterData)
  })

  return {
    hitters: Array.from(hittersMap.values()),
    teamBattedBalls,
  }
})

// --- PRINTER FRIENDLY HITTING REPORT ---
const printHittingSummary = () => {
  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    console.error('Pop-up blocked. Could not open print window.')
    return
  }
  const teamName = props.teamLineup === 'home' ? gameStore.homeTeamName : gameStore.visitingTeamName

  // Base HTML and Print-Specific CSS
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Hitting Report - ${teamName}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; line-height: 1.4; color: #000; padding: 20px; font-size: 12px; }
        h1 { margin-top: 0; padding-bottom: 10px; border-bottom: 2px solid #000; }

        .team-spray { text-align: center; margin-bottom: 20px; }
        .team-spray-title { font-size: 16px; font-weight: bold; margin-bottom: 8px; color: #1976D2; }

        .hitter-card {
          page-break-inside: avoid;
          margin-bottom: 25px;
          border: 1px solid #ccc;
          padding: 15px;
          border-radius: 8px;
        }

        .header { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
        .overall-stats { font-size: 13px; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #e0e0e0; }

        .pa-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          padding-bottom: 10px;
          border-bottom: 1px dashed #f0f0f0;
        }

        .pa-info { flex: 1; }
        .pa-title { font-weight: bold; font-size: 14px; margin-bottom: 3px; }
        .pa-result { color: #1976D2; font-weight: bold; margin-bottom: 3px; }
        .pa-stats { font-size: 12px; color: #555; }

        .charts-section { display: flex; gap: 10px; }
        .chart-container {
          text-align: center;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 6px;
          background: #fff;
        }
        .chart-title { font-size: 9px; font-weight: bold; color: #1976D2; margin-bottom: 5px; letter-spacing: 0.5px; }
      </style>
    </head>
    <body>
      <h1>Hitting Report: ${teamName}</h1>
  `

  // 1. Team Batted Balls (Spray Chart)
  let teamBBCircles = ''
  activeHittingStats.value.teamBattedBalls.forEach((hit) => {
    teamBBCircles += `<circle cx="${50 + hit.x * 75}" cy="${85 + hit.y * 75}" r="3.5" fill="${hit.color}" stroke="#2c3e50" stroke-width="0.5" opacity="0.85" />`
  })

  html += `
    <div class="team-spray">
      <div class="team-spray-title">TEAM SPRAY CHART</div>
      <svg viewBox="0 0 100 100" width="150" height="150" style="background-color: #f5f5f5; border-radius: 4px; border: 1px solid #ccc;">
        <path d="M 7.6 42.6 Q 50 15 92.4 42.6 L 50 85 Z" fill="white" stroke="#ccc" stroke-width="1.5" />
        <path d="M 30 65 Q 50 50 70 65 L 50 85 Z" fill="#eee" stroke="#ccc" stroke-width="1" />
        <path d="M 50 82 L 40 72 L 50 62 L 60 72 Z" fill="none" stroke="#ccc" stroke-width="1" />
        ${teamBBCircles}
      </svg>
    </div>
  `

  // 2. Iterate Hitters
  activeHittingStats.value.hitters.forEach((hitter) => {
    let extraOverall = ''
    if (hitter.overall.droppedThirds > 0) extraOverall += ` | D3: ${hitter.overall.droppedThirds}`
    if (hitter.overall.bunts > 0) extraOverall += ` | Bunts: ${hitter.overall.bunts}`

    html += `
      <div class="hitter-card">
        <div class="header">#${hitter.uniformNumber} - ${hitter.name}</div>
        <div class="overall-stats">
          <strong>Overall Pitches Seen: ${hitter.overall.total}</strong>
          <span style="color: #666">
            (B: ${hitter.overall.balls} | SwStr: ${hitter.overall.swingingStrikes} | CStr: ${hitter.overall.calledStrikes} | Foul: ${hitter.overall.fouls} | In Play: ${hitter.overall.inPlay}${extraOverall})
          </span>
        </div>
    `

    // Iterate Plate Appearances
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hitter.plateAppearances.forEach((pa: any) => {
      let pitchGridCircles = ''
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pa.pitches.forEach((p: any) => {
        pitchGridCircles += `<circle cx="${20 + p.x * 60}" cy="${25 + p.y * 50}" r="4" fill="${p.color}" stroke="#2c3e50" stroke-width="0.5" opacity="0.85" />`
      })

      let battedBallCircles = ''
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pa.battedBalls.forEach((hit: any) => {
        battedBallCircles += `<circle cx="${50 + hit.x * 75}" cy="${85 + hit.y * 75}" r="4" fill="${hit.color}" stroke="#2c3e50" stroke-width="0.5" opacity="0.85" />`
      })

      let runnerCircles = ''
      if (pa.runners.includes('1')) runnerCircles += `<circle cx="75" cy="60" r="5" fill="#f39c12" stroke="#e67e22" stroke-width="1" />`
      if (pa.runners.includes('2')) runnerCircles += `<circle cx="50" cy="35" r="5" fill="#f39c12" stroke="#e67e22" stroke-width="1" />`
      if (pa.runners.includes('3')) runnerCircles += `<circle cx="25" cy="60" r="5" fill="#f39c12" stroke="#e67e22" stroke-width="1" />`

      let extraPa = ''
      if (pa.stats.droppedThirds > 0) extraPa += `, D3: ${pa.stats.droppedThirds}`
      if (pa.stats.bunts > 0) extraPa += `, Bunts: ${pa.stats.bunts}`

      html += `
        <div class="pa-row">
          <div class="pa-info">
            <div class="pa-title">Inn ${pa.inning} vs ${pa.pitcherName}</div>
            <div class="pa-result">Result: ${pa.result}</div>
            <div class="pa-stats">
              Pitches Seen: <strong>${pa.stats.total}</strong><br>
              (B: ${pa.stats.balls}, SwStr: ${pa.stats.swingingStrikes}, CStr: ${pa.stats.calledStrikes}, F: ${pa.stats.fouls}${extraPa})
            </div>
          </div>
          <div class="charts-section">
            <div class="chart-container">
              <div class="chart-title">PITCHES</div>
              <svg viewBox="0 0 100 100" width="50" height="50" style="background-color: #f5f5f5; border-radius: 4px;">
                <rect x="20" y="25" width="60" height="50" fill="white" stroke="#333" stroke-width="1.5" />
                <line x1="20" y1="41.6" x2="80" y2="41.6" stroke="#999" stroke-width="0.5" />
                <line x1="20" y1="58.3" x2="80" y2="58.3" stroke="#999" stroke-width="0.5" />
                <line x1="40" y1="25" x2="40" y2="75" stroke="#999" stroke-width="0.5" />
                <line x1="60" y1="25" x2="60" y2="75" stroke="#999" stroke-width="0.5" />
                ${pitchGridCircles}
              </svg>
            </div>
            <div class="chart-container">
              <div class="chart-title">BATTED</div>
              <svg viewBox="0 0 100 100" width="50" height="50" style="background-color: #f5f5f5; border-radius: 4px;">
                <path d="M 7.6 42.6 Q 50 15 92.4 42.6 L 50 85 Z" fill="white" stroke="#ccc" stroke-width="1.5" />
                <path d="M 30 65 Q 50 50 70 65 L 50 85 Z" fill="#eee" stroke="#ccc" stroke-width="1" />
                <path d="M 50 82 L 40 72 L 50 62 L 60 72 Z" fill="none" stroke="#ccc" stroke-width="1" />
                ${battedBallCircles}
              </svg>
            </div>
            <div class="chart-container">
              <div class="chart-title">RUNNERS</div>
              <svg viewBox="0 0 100 100" width="50" height="50" style="background-color: #f5f5f5; border-radius: 4px;">
                <path d="M 50 85 L 25 60 L 50 35 L 75 60 Z" fill="none" stroke="#ccc" stroke-width="1.5" />
                ${runnerCircles}
              </svg>
            </div>
          </div>
        </div>
      `
    })

    html += `</div>` // Close hitter-card
  })

  // Auto-trigger print dialog when the window finishes loading
  html +=
    ' <script> ' +
    ' window.onload = () => { ' +
    ' window.print() } ' +
    ' <\u002Fscript><\u002Fbody><\u002Fhtml>'

  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
}
</script>
<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}
.top-z {
  z-index: 10;
}
</style>

